import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Props {
  type: 'clients' | 'invoices' | 'aiEstimate';
  used?: number;
  limit?: number | null;
  planLabel?: string;
  /** When true renders a full-page blocking layout; when false renders an inline banner */
  fullPage?: boolean;
}

export default function QuotaExceededBanner({ type, used, limit, planLabel, fullPage = false }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleViewPlans(e: React.MouseEvent) {
    e.preventDefault();
    if (confirm(t('subscription.quota.confirmLeave'))) {
      navigate('/subscription');
    }
  }

  const titleKey =
    type === 'clients'
      ? 'subscription.quota.clientsExceededTitle'
      : type === 'invoices'
        ? 'subscription.quota.invoicesExceededTitle'
        : 'subscription.quota.aiRequiresProTitle';

  const descKey =
    type === 'clients'
      ? 'subscription.quota.clientsExceededDesc'
      : type === 'invoices'
        ? 'subscription.quota.invoicesExceededDesc'
        : 'subscription.quota.aiRequiresProDesc';

  const title = t(titleKey, { used, limit, plan: planLabel });
  const desc = t(descKey, { used, limit, plan: planLabel });

  if (fullPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 max-w-md w-full shadow-sm">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 text-sm mb-6">{desc}</p>
          <Link
            to="/subscription"
            onClick={handleViewPlans}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            {t('subscription.quota.viewPlans')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
      <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-800">{title}</p>
        <p className="text-xs text-amber-700 mt-0.5">{desc}</p>
      </div>
      <Link
        to="/subscription"
        onClick={handleViewPlans}
        className="flex-shrink-0 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg transition-colors"
      >
        {t('subscription.quota.viewPlans')}
      </Link>
    </div>
  );
}
